import React, { useState } from 'react';
import { Pressable, View } from 'react-native';
import { useIsFocused, useRoute } from '@react-navigation/native';
import { useQuery } from '@apollo/client';
import { Feather } from '@expo/vector-icons';
import Toast from 'react-native-root-toast';

import { ROLE_PERMISSIONS, STATUS } from '../../../../common/constants';
import { useUserContext } from '../../../../common/utils/context';
import { areAllDocketsComplete } from '../../../../common/utils/myDay/driverDaySteps';
import { TextInput } from '../../../components';
import Text from '../../../components/text/Text';
import { PrimaryButton, Sheet } from '../../../design/components';
import { GET_SERVICE_NOTE_DOCKET, SET_SERVICE_DOCKET_SERVICE_NOTE } from '../../../graphql/serviceDocket';
import { useThemedStyles } from '../../../hooks/useThemedStyles';
import { useOfflineMutation, useOfflineQueue } from '../../../offline';
import { useTheme } from '../../../theme';
import { useDriverDay } from '../DriverDayProvider';

const ServiceNoteButton = () => {
	const route = useRoute();
	const isFocused = useIsFocused();
	const docketId = route.params?.docketId;
	const day = useDriverDay();
	const dayRun = day.todaysRuns.find(run => run.serviceDockets?.some(item => item.id === docketId));
	const dayDocket = dayRun?.serviceDockets.find(item => item.id === docketId);
	const { data } = useQuery(GET_SERVICE_NOTE_DOCKET, {
		variables: { id: docketId },
		skip: !docketId || !isFocused || Boolean(dayDocket),
		fetchPolicy: 'cache-and-network',
		errorPolicy: 'all',
	});
	// Today's loaded stops must also offer the note when first opened offline.
	const docket = dayDocket ? { ...dayDocket, serviceRun: dayRun } : data?.getServiceDocket;
	const { user, hasPermission, isSuperAdmin } = useUserContext();
	const { queue } = useOfflineQueue();
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();
	const [isOpen, setIsOpen] = useState(false);
	const [note, setNote] = useState('');
	const [isSaving, setIsSaving] = useState(false);
	const [error, setError] = useState(null);
	const pendingNote = [...queue].reverse().find(entry => entry.configKey === 'setServiceDocketServiceNote' && entry.variables?.id === docketId);
	// Keep edits in order if connection returns before an earlier save has synced.
	const [saveNote] = useOfflineMutation(SET_SERVICE_DOCKET_SERVICE_NOTE, {
		configKey: 'setServiceDocketServiceNote',
		forceQueue: Boolean(pendingNote),
	});
	// A refetch must not replace text that is still waiting to reach the server.
	const savedNote = pendingNote ? pendingNote.variables.note.trim() : docket?.serviceNote || '';
	const isCompleted = areAllDocketsComplete(docket?.serviceRun?.serviceDockets);
	const isSkipped = docket?.status !== STATUS.COMPLETE && Boolean(docket?.couldNotComplete?.at);
	const canWrite = Boolean(docket?.serviceRun) && !isCompleted && !isSkipped &&
		(docket.serviceRun.driver?.id === user?.id || isSuperAdmin || hasPermission(ROLE_PERMISSIONS.ADD_EDIT_SERVICE_RUNS));

	if (!docket || (isSkipped && !savedNote) || (!canWrite && !savedNote)) {
		return null;
	}

	const open = () => {
		setNote(savedNote);
		setError(null);
		setIsOpen(true);
	};
	const close = () => {
		if (!isSaving) {
			setIsOpen(false);
		}
	};
	const done = async () => {
		if (!canWrite || note.trim() === savedNote) {
			close();
			return;
		}
		setIsSaving(true);
		setError(null);
		try {
			const result = await saveNote({
				variables: { id: docketId, note: note.trim() },
				context: { offlineMeta: { machineName: docket.machine?.assetId } },
			});
			setIsOpen(false);
			if (result.offline) {
				Toast.show('Service note saved on phone; will sync when back online');
			}
		}
		catch (saveError) {
			setError(saveError.message || 'Unable to save the service note');
		}
		finally {
			setIsSaving(false);
		}
	};

	return (
		<>
			<Pressable
				onPress={open}
				accessibilityRole="button"
				accessibilityLabel={`Service note${savedNote ? ', note written' : ''}${canWrite ? '' : ', read only'}`}
				style={({ pressed }) => [styles.button, pressed && styles.pressed]}
			>
				<Feather name={canWrite ? 'file-text' : 'lock'} size={14} color={colors.accent} />
				<Text style={styles.buttonText}>Service note</Text>
				{Boolean(savedNote) && <View style={styles.dot} />}
			</Pressable>
			<Sheet
				isVisible={isOpen && isFocused}
				onClose={close}
				title="Service note"
				fullHeight={true}
				scroll={false}
				footer={<PrimaryButton label="Done" onPress={done} loading={isSaving} />}
			>
				<Text style={styles.subtitle}>{['This visit', docket.machine?.location?.name, docket.machine?.assetId].filter(Boolean).join(' · ')}</Text>
				<Text style={styles.hint}>{canWrite
					? 'Anything your manager needs to know from this service. You can edit it until the run is completed, then it goes on your debrief.'
					: isCompleted ? 'This run is completed. Your service note is locked and appears on your debrief.' : 'This service note is read only.'}</Text>
				<TextInput
					value={note}
					onChange={setNote}
					multiline={true}
					maxLength={2000}
					disabled={!canWrite || isSaving}
					accessibilityLabel="Service note text"
					placeholder="Anything your manager needs to know…"
					style={styles.input}
				/>
				{Boolean(error) && <Text style={styles.error} accessibilityRole="alert">{error}</Text>}
			</Sheet>
		</>
	);
};

const createStyles = ({ ui: { colors, type, space, radius, touch } }) => ({
	button: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.xs,
		minHeight: touch.min,
		paddingHorizontal: space.sm,
		borderRadius: radius.pill,
		backgroundColor: colors.chip,
	},
	buttonText: { ...type.caption, color: colors.text },
	dot: { width: 6, height: 6, borderRadius: radius.pill, backgroundColor: colors.primary },
	pressed: { opacity: 0.7 },
	subtitle: { ...type.bodySmall, color: colors.textSecondary },
	hint: { ...type.bodySmall, color: colors.textSecondary, marginBottom: space.sm },
	input: { flex: 1, minHeight: 120, borderColor: colors.accent },
	error: { ...type.bodySmall, color: colors.danger },
});

export default ServiceNoteButton;
